// select — wireframe skin module
// Token disposition: color tokens overridden to wf-* namespace; spacing/sizing pass-through

export const styles = `
  :host {
    display: inline-flex;
    flex-direction: column;
    min-inline-size: var(--run-select-min-width, 10rem);
    position: relative;
    font-family: var(--run-wf-font-family);
  }

  [part="trigger"] {
    display: inline-flex;
    align-items: center;
    gap: var(--run-select-gap, var(--run-space-semantic-xs));
    inline-size: 100%;
    block-size: var(--run-select-height, 40px);
    padding-block: var(--run-select-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-select-padding-inline, var(--run-space-semantic-md));
    background: var(--run-wf-surface);
    color: var(--run-wf-text);
    border: 1px dashed var(--run-wf-border);
    border-radius: var(--run-select-border-radius, var(--run-radius-semantic-control));
    font-family: var(--run-wf-font-family);
    font-size: var(--run-select-font-size, var(--run-font-size-base));
    font-weight: 400;
    line-height: var(--run-select-line-height, var(--run-font-semantic-line-height-body));
    cursor: pointer;
    text-align: start;
    appearance: none;
    box-shadow: none;
    transition: none;
    anchor-name: --run-select-trigger;
  }

  [part="trigger"]:hover:not(:disabled) {
    border-color: var(--run-wf-border);
  }

  [part="trigger"]:focus-visible {
    outline: var(--run-select-focus-ring-width, 2px) dashed var(--run-wf-border);
    outline-offset: var(--run-select-focus-ring-offset, 2px);
    border-color: var(--run-wf-border);
    box-shadow: none;
  }

  :host([open]) [part="trigger"] {
    border-color: var(--run-wf-border);
    box-shadow: none;
  }

  :host([invalid]) [part="trigger"] {
    border-color: var(--run-wf-border);
  }

  :host([disabled]) [part="trigger"] {
    opacity: 0.4;
    cursor: not-allowed;
    border-color: var(--run-wf-border);
    background: var(--run-wf-surface);
    color: var(--run-wf-text-subtle);
  }

  :host([readonly]) [part="trigger"] {
    cursor: default;
  }

  .trigger__label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .trigger__label--placeholder {
    color: var(--run-wf-text-subtle);
  }

  .trigger__chevron {
    flex-shrink: 0;
    inline-size: var(--run-select-icon-size, var(--run-size-icon-sm));
    block-size: var(--run-select-icon-size, var(--run-size-icon-sm));
    color: var(--run-wf-text-subtle);
    transition: none;
  }

  :host([disabled]) .trigger__chevron {
    color: var(--run-wf-text-subtle);
  }

  :host([open]) .trigger__chevron {
    transform: rotate(180deg);
  }

  [part="listbox"] {
    display: none;
  }

  [part="listbox"]:popover-open {
    display: flex;
    flex-direction: column;
    position: fixed;
    position-anchor: --run-select-trigger;
    inset-block-start: anchor(bottom);
    inset-inline-start: anchor(left);
    inline-size: anchor-size(width);
    min-inline-size: var(--run-select-listbox-min-width, anchor-size(width));
    max-block-size: var(--run-select-listbox-max-height, 18rem);
    overflow-y: auto;
    background: var(--run-wf-surface);
    border: 1px dashed var(--run-wf-border);
    border-radius: var(--run-select-listbox-border-radius, var(--run-radius-semantic-control));
    box-shadow: none;
    padding-block: var(--run-select-listbox-padding-block, var(--run-space-semantic-2xs));
    margin: 0;
    padding-inline: 0;
    list-style: none;
    transition: none;
  }

  /* RUN-376 R2: robust open-state visibility without Popover/anchor API. */
  :host([open]) [part="listbox"] {
    display: flex;
    flex-direction: column;
    position: absolute;
    inset-block-start: calc(100% + var(--run-space-semantic-2xs));
    inset-inline-start: 0;
    inline-size: 100%;
    min-inline-size: var(--run-select-listbox-min-width, 100%);
    max-block-size: var(--run-select-listbox-max-height, 18rem);
    overflow-y: auto;
    z-index: 1000;
    background: var(--run-wf-surface);
    border: 1px dashed var(--run-wf-border);
    border-radius: var(--run-select-listbox-border-radius, var(--run-radius-semantic-control));
    box-shadow: none;
    padding-block: var(--run-select-listbox-padding-block, var(--run-space-semantic-2xs));
    margin: 0;
    padding-inline: 0;
    list-style: none;
    transition: none;
  }

  @media (prefers-reduced-motion: reduce) {
    [part="trigger"],
    .trigger__chevron,
    [part="listbox"]:popover-open {
      transition: none;
    }
  }

  ::slotted([role="option"]) {
    display: flex;
    align-items: center;
    gap: var(--run-select-gap, var(--run-space-semantic-xs));
    padding-block: var(--run-select-option-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-select-option-padding-inline, var(--run-space-semantic-md));
    font-size: var(--run-select-option-font-size, var(--run-font-size-base));
    font-family: var(--run-wf-font-family);
    font-weight: 400;
    color: var(--run-wf-text);
    background: var(--run-wf-surface);
    cursor: pointer;
    user-select: none;
  }

  ::slotted([role="option"]:hover) {
    background: var(--run-wf-surface-hover);
  }

  ::slotted([role="option"][aria-selected="true"]) {
    background: var(--run-wf-surface-selected);
    color: var(--run-wf-text);
  }

  ::slotted([role="option"][aria-selected="true"]:hover) {
    background: var(--run-wf-surface-selected);
  }

  ::slotted([role="option"][aria-disabled="true"]) {
    color: var(--run-wf-text-subtle);
    cursor: not-allowed;
    opacity: 0.4;
  }

  .option-check {
    inline-size: var(--run-select-option-check-size, var(--run-size-icon-sm));
    block-size: var(--run-select-option-check-size, var(--run-size-icon-sm));
    color: var(--run-wf-accent);
    flex-shrink: 0;
  }

  .listbox__empty {
    padding-block: var(--run-select-option-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-select-option-padding-inline, var(--run-space-semantic-md));
    color: var(--run-wf-text-subtle);
    font-size: var(--run-select-option-font-size, var(--run-font-size-base));
    font-family: var(--run-wf-font-family);
    font-weight: 400;
    cursor: default;
  }
`;

export function render(host) {
  const {
    value,
    placeholder,
    size,
    open,
    disabled,
    readonly,
    invalid,
    _activeDescendantId,
    shadowRoot,
  } = host;

  const hasValue = value !== '' && value != null;
  const labelClass = hasValue ? 'trigger__label' : 'trigger__label trigger__label--placeholder';
  const labelText = hasValue ? value : (placeholder || '');

  const expandedAttr = open ? 'true' : 'false';
  const disabledAttr = disabled ? 'true' : 'false';
  const requiredAttr = host.required ? 'true' : 'false';
  const invalidAttr = invalid ? 'true' : 'false';
  const readonlyAttr = readonly ? 'true' : 'false';
  const tabindexAttr = disabled ? '-1' : '0';
  const activedescAttr = _activeDescendantId ? ` aria-activedescendant="${_activeDescendantId}"` : '';

  const chevronSvg = `<svg class="trigger__chevron" aria-hidden="true" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  shadowRoot.innerHTML = `
    <button
      class="trigger"
      part="trigger"
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded="${expandedAttr}"
      aria-controls="listbox"
      aria-disabled="${disabledAttr}"
      aria-required="${requiredAttr}"
      aria-invalid="${invalidAttr}"
      aria-readonly="${readonlyAttr}"
      ${activedescAttr}
      tabindex="${tabindexAttr}"
      ${disabled ? 'disabled' : ''}
    >
      <slot name="prefix"></slot>
      <span class="${labelClass}">
        <slot name="placeholder">${labelText}</slot>
      </span>
      ${chevronSvg}
    </button>
    <div
      id="listbox"
      class="listbox"
      part="listbox"
      role="listbox"
      popover="manual"
    >
      <slot></slot>
    </div>
  `;
}